import React, { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card, CardBody, CardTitle } from './ui/Card';
import { AlertMessage } from './ui/AlertMessage';
import { Spinner } from './ui/Spinner';
import { useModuleAccess } from '../permissions/useModuleAccess';
import { ModuleAccessBanner } from './ui/ModuleAccessBanner';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export default function Khatas() {
  const { canCreate, canEdit } = useModuleAccess('PRODUCTION_UNITS');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [newName, setNewName] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [machineForms, setMachineForms] = useState({}); // khataId -> { machineNo, label }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/khatas?all=true`, { headers: authHeaders() });
      const text = await res.text();
      const body = text ? JSON.parse(text) : [];
      if (!res.ok) throw new Error(body?.error || body?.message || 'Failed to load production units');
      setItems(Array.isArray(body) ? body : []);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addKhata = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/khatas`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name, active: true }),
      });
      const text = await res.text();
      const body = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error(body?.error || body?.message || 'Save failed');
      setNewName('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (khata, nextActive) => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/khatas/${khata.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ name: khata.name, active: nextActive }),
      });
      const text = await res.text();
      const body = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error(body?.error || body?.message || 'Update failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const addMachine = async (khataId) => {
    const f = machineForms[khataId] || { machineNo: '', label: '' };
    const machineNo = String(f.machineNo || '').trim();
    if (!machineNo) {
      setError('Enter a machine number');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/khatas/${khataId}/machines`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          machineNo,
          label: String(f.label || '').trim() || undefined,
          active: true,
        }),
      });
      const text = await res.text();
      const body = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error(body?.error || body?.message || 'Failed to add machine');
      setMachineForms((prev) => ({ ...prev, [khataId]: { machineNo: '', label: '' } }));
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const deactivateMachine = async (khataId, machineId) => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/khatas/${khataId}/machines/${machineId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const text = await res.text();
        const body = text ? JSON.parse(text) : {};
        throw new Error(body?.error || 'Failed');
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && items.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-black text-slate-900">Production Units &amp; machines</h1>
      </div>

      <ModuleAccessBanner moduleKey="PRODUCTION_UNITS" />
      {error ? <AlertMessage type="error" message={error} onClose={() => setError('')} /> : null}

      {canCreate && (
        <Card className="border-slate-200 shadow-sm">
          <CardBody className="p-4 sm:p-6 space-y-4">
            <CardTitle className="text-base">Add production unit</CardTitle>
            <form onSubmit={addKhata} className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <Input
                  label="Name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Shree Laxmikant textile"
                  disabled={busy}
                />
              </div>
              <Button type="submit" variant="primary" disabled={busy || !newName.trim()}>
                Add
              </Button>
            </form>
          </CardBody>
        </Card>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <p className="text-sm text-slate-500">
            {canCreate ? 'No production units yet. Add one above.' : 'No production units to display.'}
          </p>
        ) : (
          items.map((k) => {
            const open = expandedId === k.id;
            const machines = Array.isArray(k.machines) ? k.machines.filter((m) => m.active !== false) : [];
            const mf = machineForms[k.id] || { machineNo: '', label: '' };
            return (
              <Card key={k.id} className={`border shadow-sm ${k.active === false ? 'opacity-60' : ''}`}>
                <CardBody className="p-4 sm:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-lg font-bold text-slate-900">{k.name}</p>
                        {k.global ? (
                          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-800 ring-1 ring-sky-200">
                            Global
                          </span>
                        ) : null}
                      </div>
                      <p className="text-xs text-slate-500">
                        {k.active === false ? 'Inactive' : 'Active'}
                        {machines.length ? ` · ${machines.length} machine(s)` : ' · No machines yet'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setExpandedId(open ? null : k.id)}
                      >
                        {open ? 'Hide machines' : 'Machines'}
                      </Button>
                      {canEdit && (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={busy || k.global}
                          title={k.global ? 'Built-in production units cannot be deactivated' : undefined}
                          onClick={() => toggleActive(k, k.active === false)}
                        >
                          {k.active === false ? 'Activate' : 'Deactivate'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {open && (
                    <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
                      <div className="grid gap-2 sm:grid-cols-2">
                        {machines.length === 0 ? (
                          <p className="text-sm text-slate-500 sm:col-span-2">No active machines. Add below.</p>
                        ) : (
                          machines.map((m) => (
                            <div
                              key={m.id}
                              className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm"
                            >
                              <span className="font-semibold text-slate-800">
                                {m.machineNo}
                                {m.label ? <span className="font-normal text-slate-600"> — {m.label}</span> : null}
                              </span>
                              {canEdit && (
                                <button
                                  type="button"
                                  className="text-xs font-semibold text-rose-600 hover:underline"
                                  disabled={busy}
                                  onClick={() => deactivateMachine(k.id, m.id)}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                      {canCreate && (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                          <Input
                            label="Machine no."
                            value={mf.machineNo}
                            onChange={(e) =>
                              setMachineForms((prev) => ({
                                ...prev,
                                [k.id]: { ...mf, machineNo: e.target.value },
                              }))
                            }
                            placeholder="e.g. M-01"
                            disabled={busy}
                          />
                          <Input
                            label="Label (optional)"
                            value={mf.label}
                            onChange={(e) =>
                              setMachineForms((prev) => ({
                                ...prev,
                                [k.id]: { ...mf, label: e.target.value },
                              }))
                            }
                            placeholder="e.g. Front hall"
                            disabled={busy}
                          />
                          <Button
                            type="button"
                            variant="primary"
                            disabled={busy || k.active === false}
                            onClick={() => addMachine(k.id)}
                          >
                            Add machine
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
